import { Point } from '../../../../../FrontendBundle/Resources/js/ofcs';
import ContainerItem from '../item/containerItem';
import InPolyCollision from './collision/inPolyCollision';
import IntersectionCollision from './collision/intersectionCollision';

/**
 * @extends CollisionManager
 */
export default class OutOfContainerChecker {
    /**
     * @type {PolygonCollisionChecker}
     */
    polygonCollisionChecker = null;

    /**
     * Initializes the collision checker
     *
     * @param {PolygonCollisionChecker} polygonCollisionChecker
     * @param {TreeCollisionChecker} treeCollisionChecker
     * @param {CollisionExceptionService} exceptionService
     */
    constructor(polygonCollisionChecker) {
        this.polygonCollisionChecker = polygonCollisionChecker;
    }

    /**
     * Check the given item against all its siblings
     *
     * @param {AbstractItem} container
     * @param {AbstractItem} item
     * @return {AbstractCollision} Array with found collisions
     */
    check(container, item) {
        if (container.cid === item.cid) return null;

        const [leadingItem, otherItem] = this._sortItemsBySafetyMargin([container, item]);
        const [normalizedLeadingItem, normalizedOtherItem] = this._normalizeContainers([leadingItem, otherItem]);
        const [normalizedContainer] = this._normalizeContainers([container]);

        const leadingTree = this._getLeadingTree(normalizedLeadingItem, normalizedOtherItem);
        const otherTree = this._getOtherTree(normalizedOtherItem);

        let intersections = leadingTree.intersect(otherTree);

        if (intersections.count() === 0 && this._supportBidirectionalCollisions(leadingItem, otherItem)) {
            intersections = otherTree.intersect(leadingTree);
        }

        if (intersections.count() > 0) {
            return new IntersectionCollision(leadingItem, otherItem, intersections);
        }

        if (this._areItemsInEachOther(normalizedContainer, item)) {
            return new InPolyCollision(leadingItem, otherItem);
        }

        return null;
    }

    /**
     * @param {AbstractItem[]} items
     * @return {AbstractItem[]}
     * @private
     */
    _sortItemsBySafetyMargin(items) {
        return items.sort((a, b) => b.getSafetyMargin() - a.getSafetyMargin());
    }

    /**
     * @param {AbstractItem[]} items
     * @return {AbstractItem[]}
     * @private
     */
    _normalizeContainers(items) {
        return items.map((item) => {
            if (item instanceof ContainerItem) {
                const clone = item.clone();
                clone.set('position', new Point(0, 0));
                return clone;
            }
            return item;
        });
    }

    /**
     * @param {AbstractItem} leadingItem
     * @param {AbstractItem} otherItem
     * @return {Tree}
     * @private
     */
    _getLeadingTree(leadingItem, otherItem) {
        return this._supportMarginCollisions(leadingItem, otherItem)
            ? leadingItem.getMarginTree()
            : leadingItem.getBagTree();
    }

    /**
     * @param {AbstractItem} otherItem
     * @return {Tree}
     * @private
     */
    _getOtherTree(otherItem) {
        return otherItem.getBagTree();
    }

    /**
     * @param {AbstractItem} item1
     * @param {AbstractItem} item2
     * @return {boolean}
     * @private
     */
    _supportBidirectionalCollisions(item1, item2) {
        return item1.supportsBidirectionalCollisions || item2.supportsBidirectionalCollisions;
    }

    /**
     * @param {AbstractItem} item1
     * @param {AbstractItem} item2
     * @return {boolean}
     * @private
     */
    _supportMarginCollisions(item1, item2) {
        return item1.supportsMarginCollisions && item2.supportsMarginCollisions;
    }

    /**
     * Are the bounds of the foam tree inside the other tee?
     *
     * @param {AbstractItem} container
     * @param {AbstractItem} item
     * @return {boolean}
     * @private
     */
    _areItemsInEachOther(container, item) {
        const containerBounds = container.getBounds();
        const otherBounds = item.getMarginBounds();
        const otherPosition = item.getPosition();

        // !(!a && !b && c) should be a bit faster than (a || b || !c), as ideally not all conditions have to be checked
        return !(
            !this.polygonCollisionChecker.isPointInPoly(otherPosition, container.getMarginLines())
            && !otherBounds.contains(containerBounds)
            && this.polygonCollisionChecker.isPointInPoly(otherPosition, container.getBagLines())
        );
    }
}
